import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ICustomFieldColumn } from "../../../../@types/CustomField";
import { Notification } from "../../../../@types/Notification";
import { PatientListConfig } from "../../../../@types/Patient";
import { IUpdatePortalPreferences } from "../../../../@types/User";
import { Button } from "../../../../components/Button";
import { Checkbox } from "../../../../components/Checkbox";
import {
  useApplicableRagMeasurements,
  useRagAvailableMeasurements,
} from "../../../../hooks/queries/ragConfig";
import { userService } from "../../../../services/userService";
import { customColumnConfigKey } from "../../../../utils/customFields";
import { mixpanelActions } from "../../../../utils/mixpanel";
import {
  Subtitle,
  Title,
} from "../../../PatientPage/components/PatientLateralMenu/components/ConfirmChangesModal.styles";
import { CancelButton } from "../../../Settings/components/SecurityModal/SecurityModal.styles";
import {
  ColumnTitle,
  Container,
  FooterContainer,
  HeaderRow,
  Row,
  ScrollableContent,
  SectionDivider,
} from "./PatientListConfigModal.styles";

// EN-5123: "RAG MEASUREMENTS (MAX 3)" — a staff member can show at most three RAG columns. The
// server caps this too (PatientListColumnSetParser), so a config saved before the cap existed still
// yields three columns; here we stop them adding a fourth.
const MAX_RAG_MEASUREMENTS = 3;

type PatientListConfigProps = {
  columnsConfig: PatientListConfig;
  // EN-5067: custom fields available as optional columns (union across the staff's wards).
  customColumns?: ICustomFieldColumn[];
  onClose(
    listColumnConfig?: PatientListConfig,
    notification?: Notification,
    refreshTable?: boolean
  ): void;
};

export function PatientListConfigModal({
  columnsConfig,
  customColumns = [],
  onClose,
}: PatientListConfigProps) {
  const { t } = useTranslation();
  const [confirmChanges, setConfirmChanges] = useState(false);
  const [listColumnConfig, setListColumnConfig] =
    useState<PatientListConfig>(columnsConfig);
  const { applicableMeasurements, isRagEnabled } =
    useApplicableRagMeasurements();
  // Display names and units for the measurements available on this staff member's own wards.
  const { byCode: measurementsByCode } = useRagAvailableMeasurements();

  useEffect(() => {
    mixpanelActions.track("Visited Screen: PatientListConfiguration");
  }, []);

  // One row per measurement banded by a published config that applies to this user, labelled from
  // the ward measurement-type catalog. The config JSON key IS the measurement code.
  const visibleRagRows = isRagEnabled
    ? Array.from(applicableMeasurements).map((code) => {
        const meta = measurementsByCode.get(code);
        const name = meta?.measurementTypeDisplayName || code;
        return {
          code,
          label: meta?.unit ? `${name} (${meta.unit})` : name,
        };
      })
    : [];

  const selectedRagCount = visibleRagRows.filter(
    (row) => !!listColumnConfig[row.code]
  ).length;
  const ragLimitReached = selectedRagCount >= MAX_RAG_MEASUREMENTS;

  const handleValueChange = (value: boolean, propertyName: string) => {
    mixpanelActions.track(`User Action: Checkbox ${propertyName}`);

    setListColumnConfig((prev) => ({
      ...prev,
      [propertyName as keyof PatientListConfig]: value,
    }));
  };

  const handleOnConfirm = async () => {
    mixpanelActions.track("User Action: ConfigurePatientListConfirm");
    if (confirmChanges) {
      const tableConfigJson = JSON.stringify(listColumnConfig);

      const portalPreferences: IUpdatePortalPreferences = {
        patientListConfig: tableConfigJson,
      };

      const result = await userService.updatePatientListConfig(
        portalPreferences
      );

      if (result.status >= 200 && result.status < 300) {
        onClose(
          listColumnConfig,
          {
            show: true,
            message: t("patientList_configModal_saved"),
            type: "success",
            width: "max-content",
          },
          true
        );
        return;
      }

      onClose(
        undefined,
        {
          show: true,
          message: t("common_genericError"),
          type: "error",
          width: "max-content",
        },
        true
      );
    }

    setConfirmChanges(true);
  };

  return (
    <Container compact={confirmChanges}>
      {!confirmChanges ? (
        <HeaderRow>
          <ColumnTitle>{t("patientList_configModal_columnHeader")}</ColumnTitle>
          <ColumnTitle>{t("patientList_configModal_showHeader")}</ColumnTitle>
        </HeaderRow>
      ) : null}

      <ScrollableContent compact={confirmChanges}>
        {!confirmChanges ? (
          <>
            <Row disabled>
              <p>{t("patientList_columns_name")}</p>
              <Checkbox
                checked={listColumnConfig.name}
                onChange={() => {}}
                label=""
                labelPosition="before"
                disabled
              />
            </Row>

            <Row>
              <p>{t("patientList_columns_hospitalId")}</p>
              <Checkbox
                checked={!!listColumnConfig.hospitalId}
                onChange={(isChecked) =>
                  handleValueChange(isChecked, "hospitalId")
                }
                label=""
                labelPosition="before"
              />
            </Row>

            <Row>
              <p>{t("patientList_columns_pmpId")}</p>
              <Checkbox
                checked={!!listColumnConfig.pmpId}
                onChange={(isChecked) => handleValueChange(isChecked, "pmpId")}
                label=""
                labelPosition="before"
              />
            </Row>

            <Row>
              <p>{t("patientList_columns_alerts")}</p>
              <Checkbox
                checked={listColumnConfig.alerts}
                onChange={(isChecked) => handleValueChange(isChecked, "alerts")}
                label=""
                labelPosition="before"
              />
            </Row>

            <Row>
              <p>{t("patientList_columns_signUpDate")}</p>
              <Checkbox
                checked={listColumnConfig.signUpDate}
                onChange={(isChecked) =>
                  handleValueChange(isChecked, "signUpDate")
                }
                label=""
                labelPosition="before"
              />
            </Row>

            <Row>
              <p>{t("patientList_columns_lastUsed")}</p>
              <Checkbox
                checked={listColumnConfig.lastUsed}
                onChange={(isChecked) =>
                  handleValueChange(isChecked, "lastUsed")
                }
                label=""
                labelPosition="before"
              />
            </Row>

            <Row>
              <p>{t("patientList_columns_lastReviewedBy")}</p>
              <Checkbox
                checked={listColumnConfig.lastReviewedBy}
                onChange={(isChecked) =>
                  handleValueChange(isChecked, "lastReviewedBy")
                }
                label=""
                labelPosition="before"
              />
            </Row>

            <Row>
              <p>{t("patientList_columns_carerEmail")}</p>
              <Checkbox
                checked={!!listColumnConfig.carerEmail}
                onChange={(isChecked) =>
                  handleValueChange(isChecked, "carerEmail")
                }
                label=""
                labelPosition="before"
              />
            </Row>

            {listColumnConfig.surveyResults !== undefined ? (
              <Row>
                <p>{t("patientList_configModal_surveyResultBeaumont")}</p>
                <Checkbox
                  checked={listColumnConfig.surveyResults}
                  onChange={(isChecked) =>
                    handleValueChange(isChecked, "surveyResults")
                  }
                  label=""
                  labelPosition="before"
                />
              </Row>
            ) : null}

            {customColumns.length > 0 ? (
              <>
                <SectionDivider>
                  <span>{t("patientList_configModal_additionalFields")}</span>
                </SectionDivider>
                {customColumns.map((column) => {
                  const key = customColumnConfigKey(
                    column.customFieldDefinitionId
                  );
                  return (
                    <Row key={key}>
                      <p>{column.label}</p>
                      <Checkbox
                        checked={!!listColumnConfig[key]}
                        onChange={(isChecked) =>
                          handleValueChange(isChecked, key)
                        }
                        label=""
                        labelPosition="before"
                      />
                    </Row>
                  );
                })}
              </>
            ) : null}

            {visibleRagRows.length > 0 ? (
              <>
                <SectionDivider>
                  {/* SectionDivider uppercases its text, so this renders as
                      "RAG MEASUREMENTS (MAX 3)". */}
                  <span>
                    {t("patientList_configModal_ragSection", {
                      max: MAX_RAG_MEASUREMENTS,
                    })}
                  </span>
                </SectionDivider>
                {visibleRagRows.map(({ code, label }) => {
                  const checked = !!listColumnConfig[code];
                  return (
                    <Row key={code} disabled={!checked && ragLimitReached}>
                      <p>{label}</p>
                      <Checkbox
                        checked={checked}
                        onChange={(isChecked) =>
                          handleValueChange(isChecked, code)
                        }
                        label=""
                        labelPosition="before"
                        // Untick one before another can be added.
                        disabled={!checked && ragLimitReached}
                      />
                    </Row>
                  );
                })}
              </>
            ) : null}
          </>
        ) : (
          <>
            <Title>{t("patientList_configModal_confirmTitle")}</Title>
            <Subtitle larger style={{ fontWeight: "400" }}>
              {t("patientList_configModal_confirmSubtitle")}
            </Subtitle>
          </>
        )}
      </ScrollableContent>

      <FooterContainer>
        <Button
          label={t("patientList_configModal_confirm")}
          type="button"
          onClick={handleOnConfirm}
          isLoading={false}
        />

        <CancelButton
          onClick={() => {
            mixpanelActions.track("User Action: ConfigurePatientListCancel");
            onClose();
          }}
          style={{ marginLeft: "-34px" }}
        >
          {t("common_cancel")}
        </CancelButton>
      </FooterContainer>
    </Container>
  );
}
